import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { ItemStockOnHandResponse } from '../../../app/services/api/generated';
import { OrderForm, OrderFormItem } from '../../../app/services/api/orderManagementClient';
import { getInventorySku } from '../../../utils';

type ImStockStatus = 'loading' | 'ready' | 'error' | 'forbidden' | 'unavailable';

type StockSnapshot = {
  requestKey: string;
  status: 'ready' | 'error' | 'forbidden';
  stock?: Map<string, ItemStockOnHandResponse>;
};

export const useOrderFormImStock = (orderForm?: OrderForm, orderFormStoreNumber?: string) => {
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getStockForItems } = useNswagClient();
  const getStockForItemsRef = useRef(getStockForItems);
  getStockForItemsRef.current = getStockForItems;

  const storeNumber = selectedStore?.storeNumber;
  const canReadStock = hasPermissionTo(['StockRead']);
  // Quantity edits, item reordering and duplicate orderable SKUs do not refetch stock.
  const inventorySkusKey = JSON.stringify(Array.from(new Set(
    orderForm?.categories.flatMap(category => category.items.map(getInventorySku)) ?? [],
  )).filter(sku => !!sku.trim()).sort());
  const canLoad = !!storeNumber && orderFormStoreNumber === storeNumber && inventorySkusKey !== '[]';
  const requestKey = JSON.stringify([storeNumber, inventorySkusKey]);
  const [snapshot, setSnapshot] = useState<StockSnapshot>();

  useEffect(() => {
    let cancelled = false;
    setSnapshot(undefined);

    if (!canReadStock || !canLoad || !storeNumber) {
      return () => { cancelled = true; };
    }

    const load = async () => {
      try {
        const inventorySkus: string[] = JSON.parse(inventorySkusKey);
        // One POST sends every required inventory SKU in the body, without URL-length limits.
        const response = await getStockForItemsRef.current(storeNumber, inventorySkus);
        if (!Array.isArray(response)) {
          throw new Error('IM stock response missing stock rows');
        }

        const requestedItems = new Set(inventorySkus.map(sku => sku.trim().toUpperCase()));
        // IM authorizes and filters the requested store; requestKey binds this response to it.
        const byItem = new Map<string, ItemStockOnHandResponse>();
        for (const row of response) {
          const itemNumber = row.itemNumber?.trim().toUpperCase();
          if (itemNumber && requestedItems.has(itemNumber)) {
            byItem.set(itemNumber, row);
          }
        }
        if (!cancelled) setSnapshot({ requestKey, status: 'ready', stock: byItem });
      } catch (error) {
        if (cancelled) return;
        const failure = error as { status?: number; response?: { status?: number } };
        const status = failure?.status ?? failure?.response?.status;
        const denied = status === 401 || status === 403;
        setSnapshot({ requestKey, status: denied ? 'forbidden' : 'error' });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [storeNumber, inventorySkusKey, requestKey, canLoad, canReadStock]);

  const stockStatus: ImStockStatus = !canLoad ? 'unavailable'
    : !canReadStock ? 'forbidden'
      : snapshot?.requestKey === requestKey ? snapshot.status : 'loading';

  const getQuantity = useMemo(() => (item: OrderFormItem): number => {
    if (stockStatus !== 'ready' || !snapshot?.stock) return 0;
    const inventorySku = getInventorySku(item).trim().toUpperCase();
    const row = inventorySku ? snapshot.stock.get(inventorySku) : undefined;
    // IM returns primary-UOM stock. Keep valid zero and never use the OM value.
    if (row?.uom && item.primaryUom &&
        row.uom.trim().toUpperCase() === item.primaryUom.trim().toUpperCase() &&
        typeof row.stockOnHand === 'number' && Number.isFinite(row.stockOnHand)) {
      return row.stockOnHand;
    }
    return 0;
  }, [snapshot, stockStatus]);

  return { stockStatus, getQuantity };
};
